import React from 'react';
import { Button } from './ui/button';
import { Edit, LogOut } from 'lucide-react';

// TaxSetu Logo - Infinity symbol
const TAXSETU_LOGO = "https://customer-assets.emergentagent.com/job_7a5b9feb-7efd-48fe-91ba-ada6a1b3fe62/artifacts/dbzfuj5b_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

const GSTPortalDashboard = ({ trn, creationDate, onEditApplication, onLogout }) => {
  // Calculate expiry date (15 days from creation)
  const getExpiryDate = () => {
    const date = creationDate ? new Date(creationDate) : new Date();
    date.setDate(date.getDate() + 15);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getCreationDateFormatted = () => {
    const date = creationDate ? new Date(creationDate) : new Date();
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Skip to Main Content Bar - Top right */}
      <div className="bg-[#0b1e3f] text-white text-xs py-1 px-4 flex justify-end items-center gap-3">
        <span className="text-gray-300 cursor-pointer hover:underline">Skip to Main Content</span>
        <span className="flex items-center gap-2 text-gray-300">
          <span className="w-5 h-5 bg-yellow-500 rounded text-black text-xs flex items-center justify-center font-bold">0</span>
          <span className="cursor-pointer">A<sup>+</sup></span>
          <span className="cursor-pointer">A<sup>-</sup></span>
        </span>
      </div>

      {/* Main Header - Dark Blue like GST Portal */}
      <header className="bg-[#0b1e3f] text-white py-6 px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-5">
            {/* TaxSetu Logo */}
            <div className="h-14 flex-shrink-0">
              <img 
                src={TAXSETU_LOGO}
                alt="PractiQ"
                className="h-full object-contain"
              />
            </div>
            <div className="border-l border-white/30 pl-5">
              <h1 className="text-2xl font-normal tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
                Demo Goods and Services Tax
              </h1>
              <p className="text-base text-gray-300 mt-1">
                Practical Simulation - Not a Government Website - Only for Educational Purpose
              </p>
            </div>
          </div>
          <button 
            onClick={onLogout}
            className="text-white hover:text-gray-300 flex items-center gap-2 text-sm"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      {/* Navigation Bar - Blue like GST Portal */}
      <nav className="bg-[#2a5298]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center">
            <button className="px-5 py-3 bg-[#f7941d] text-white font-medium text-sm">
              Dashboard
            </button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm flex items-center gap-1">
              Services <span className="text-xs">▼</span>
            </button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">
              GST Law
            </button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm flex items-center gap-1">
              Downloads <span className="text-xs">▼</span>
            </button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm flex items-center gap-1">
              Search Taxpayer <span className="text-xs">▼</span>
            </button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">
              Help and Taxpayer Facilities
            </button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">
              e-Invoice
            </button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">
              News and Updates
            </button>
          </div>
        </div>
      </nav>

      {/* Breadcrumb Bar */}
      <div className="bg-white border-b py-2 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <span className="text-blue-700 font-medium">Dashboard</span>
          <div className="flex items-center gap-1 text-sm">
            <span className="text-red-500">📍</span>
            <span className="text-blue-600">English</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          {/* My Saved Applications Section */}
          <div className="bg-white rounded shadow-sm mb-6">
            <div className="border-b px-4 py-3">
              <h3 className="text-lg font-semibold text-gray-800">My Saved Applications</h3>
            </div>
            <div className="p-4 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Creation Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Form No.</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Form Description</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Expiry Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-600">{getCreationDateFormatted()}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">GST REG-01</td>
                    <td className="py-3 px-4 text-sm text-gray-600">Application for New Registration</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{getExpiryDate()}</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                        Draft
                        <span className="w-4 h-4 bg-blue-500 rounded-full text-white text-xs flex items-center justify-center">i</span>
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={onEditApplication}
                        className="w-8 h-8 bg-[#6c7ae0] hover:bg-[#5a68d4] text-white rounded flex items-center justify-center mx-auto"
                        data-testid="edit-application-btn"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Track Application Status Section */}
          <div className="bg-white rounded shadow-sm">
            <div className="border-b px-4 py-3">
              <h3 className="text-lg font-semibold text-gray-800">Track Application Status</h3>
            </div>
            <div className="p-4">
              <p className="text-gray-500 text-sm">You do not have any submitted applications</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer - Dark Blue */}
      <footer className="bg-[#2a5298] text-white py-4 px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-xs text-white/80 mb-2">
            This is a Practice Simulation Portal by PractiQ for educational purposes only. 
            Not affiliated with or endorsed by any Government entity.
          </p>
          <div className="flex flex-wrap items-center justify-between text-xs text-white/60">
            <span>© 2024-25 PractiQ - EdTech Platform</span>
            <span>Site Last Updated on {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
            <span>For Practice Purpose Only</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GSTPortalDashboard;
